import React, { useEffect, useMemo, useState } from "react";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import TablePageHeading from "../page_heading/TablePageHeading";
import CardTabs from "../cards/CardTabs";
import TableList from "../table/TableList";
import { Button, Col, Row } from "../../../../components/Component";
import { Spinner } from "reactstrap";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BiReceipt,
  BiCheckCircle,
  BiTime,
  BiErrorCircle,
  BiRefresh,
  BiPlus,
  BiBox,
  BiCog,
} from "react-icons/bi";
import { listSmartInvoices } from "../../../../services/smart_invoice_service";

const type = "SMART_INVOICE_LIST";

const toNum = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeStatus = (status = "") => String(status).trim().toLowerCase();

const SmartInvoiceList = () => {
  const [invoiceRows, setInvoiceRows] = useState([]);
  const [activeCardTab, setActiveCardTab] = useState("All");
  const [error, setError] = useState("");
  const [dataFetched, setDataFetched] = useState(false);
  const [loading, setLoading] = useState(true);

  const cardMotion = useMemo(
    () => ({
      hidden: { opacity: 0, y: 12, scale: 0.985 },
      show: (i = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.35, delay: i * 0.08, ease: "easeOut" },
      }),
    }),
    []
  );

  const hoverMotion = useMemo(
    () => ({
      whileHover: { y: -4, transition: { duration: 0.18, ease: "easeOut" } },
      whileTap: { scale: 0.99 },
    }),
    []
  );

  const retrieveSmartInvoices = async () => {
    setLoading(true);
    setError("");

    try
    {
      const rows = await listSmartInvoices();
      setInvoiceRows(rows);
      setDataFetched(true);
    } catch (err)
    {
      setError("Failed to load smart invoices.");
    } finally
    {
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieveSmartInvoices();
  }, []);

  const tabs = useMemo(
    () => [
      { tabName: "All" },
      { tabName: "Draft" },
      { tabName: "Pending" },
      { tabName: "Validated" },
      { tabName: "Failed" },
      { tabName: "Refunded" },
    ],
    []
  );

  const groupedRows = useMemo(
    () => ({
      All: invoiceRows,
      Draft: invoiceRows.filter((row) => normalizeStatus(row?.status) === "draft"),
      Pending: invoiceRows.filter((row) =>
        ["pending", "pending_retry"].includes(normalizeStatus(row?.status))
      ),
      Validated: invoiceRows.filter((row) => normalizeStatus(row?.status) === "validated"),
      Failed: invoiceRows.filter((row) => normalizeStatus(row?.status) === "failed"),
      Refunded: invoiceRows.filter((row) => normalizeStatus(row?.status) === "refunded"),
    }),
    [invoiceRows]
  );

  const activeRows = groupedRows[activeCardTab] || groupedRows.All;
  const totalRevenue = groupedRows.Validated.reduce(
    (sum, row) => sum + toNum(row?.total_due || row?.total_amount),
    0
  );

  const IconCard = ({ icon: Icon, title, amount, tone = "primary" }) => (
    <div className="card card-bordered h-100 shadow-sm">
      <div className="card-inner">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="text-soft fw-medium">{title}</div>
          <div
            className={`d-flex align-items-center justify-content-center rounded-circle bg-${tone} bg-opacity-10`}
            style={{ width: "40px", height: "40px" }}
          >
            <Icon className={`fs-4 text-${tone}`} />
          </div>
        </div>
        <div className="fs-22px fw-bold">{amount}</div>
      </div>
    </div>
  );

  return (
    <>
      <Head title="Smart Invoices" />
      <Content>
        <TablePageHeading
          title="Smart Invoices"
          refresh={!loading}
          refreshFunc={retrieveSmartInvoices}
          page_type={type}
          pageData={invoiceRows}
        />

        {loading ? (
          <div className="card card-bordered shadow-sm">
            <div className="card-inner py-5">
              <div className="d-flex flex-column align-items-center justify-content-center">
                <Spinner />
                <div className="fs-15px mt-3 text-soft">Loading Smart Invoices...</div>
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="card card-bordered shadow-sm">
            <div className="card-inner py-5 text-center">
              <BiErrorCircle className="text-danger fs-2" />
              <div className="mt-3 fw-bold text-soft">{error}</div>
              <button className="btn btn-primary mt-3" onClick={retrieveSmartInvoices} type="button">
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <Row className="g-gs">
            <Col xs="12">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
                <Row className="g-gs">
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate={dataFetched ? "show" : "hidden"} custom={0} {...hoverMotion}>
                      <IconCard icon={BiReceipt} title="Total Invoices" amount={groupedRows.All.length} tone="primary" />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate={dataFetched ? "show" : "hidden"} custom={1} {...hoverMotion}>
                      <IconCard icon={BiCheckCircle} title="Validated" amount={groupedRows.Validated.length} tone="success" />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate={dataFetched ? "show" : "hidden"} custom={2} {...hoverMotion}>
                      <IconCard icon={BiTime} title="Pending" amount={groupedRows.Pending.length} tone="warning" />
                    </motion.div>
                  </Col>
                  <Col xxl="3" sm="6">
                    <motion.div className="h-100" variants={cardMotion} initial="hidden" animate={dataFetched ? "show" : "hidden"} custom={3} {...hoverMotion}>
                      <IconCard icon={BiRefresh} title="Revenue" amount={`ZMW ${totalRevenue.toLocaleString()}`} tone="info" />
                    </motion.div>
                  </Col>
                </Row>
              </motion.div>
            </Col>

            <Col xxl="9">
              <div className="card card-bordered shadow-sm">
                <div className="card-inner border-bottom">
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-3">
                    <div>
                      <div className="fs-18px fw-bold">Smart Invoice List</div>
                      <div className="text-soft small mt-1">View, track, and manage all Smart Invoices in one place.</div>
                    </div>

                    <div className="d-flex gap-2 flex-wrap">
                      <Link to="/dashboard/smart_invoices/inventory">
                        <Button color="light" className="d-flex align-items-center">
                          <BiBox className="me-1" />
                          Inventory
                        </Button>
                      </Link>
                      <Link to="/dashboard/smart_invoices/config">
                        <Button color="light" className="d-flex align-items-center">
                          <BiCog className="me-1" />
                          Config
                        </Button>
                      </Link>
                      <Link to="/dashboard/smart_invoices/create">
                        <Button color="primary" className="d-flex align-items-center">
                          <BiPlus className="me-1" />
                          Create Smart Invoice
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="card-inner border-bottom bg-light">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <div className="text-soft small">
                      Showing: <span className="fw-bold text-dark">{activeCardTab}</span>
                    </div>

                    <CardTabs
                      cardTabs={tabs}
                      activeCardTab={activeCardTab}
                      setActiveCardTab={setActiveCardTab}
                      dataFetched={dataFetched}
                    />
                  </div>
                </div>

                <div className="card-inner p-0">
                  <div className="p-3">
                    <TableList type={type} data={activeRows} rowOptionsData={{}} />
                  </div>
                </div>
              </div>
            </Col>

            <Col xxl="3" md="6">
              <div className="card card-bordered h-100 shadow-sm">
                <div className="card-inner border-bottom">
                  <div className="fs-16px fw-bold">Summary</div>
                  <div className="text-soft small mt-1">Quick invoice breakdown</div>
                </div>

                <div className="card-inner">
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-soft">Draft</span>
                    <span className="badge bg-secondary">{groupedRows.Draft.length}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-soft">Pending</span>
                    <span className="badge bg-warning text-dark">{groupedRows.Pending.length}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-soft">Validated</span>
                    <span className="badge bg-success">{groupedRows.Validated.length}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-soft">Failed</span>
                    <span className="badge bg-danger">{groupedRows.Failed.length}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="text-soft">Refunded</span>
                    <span className="badge bg-info">{groupedRows.Refunded.length}</span>
                  </div>

                  <hr className="my-3" />

                  <div className="d-flex align-items-center gap-2 mb-3">
                    <BiReceipt className="text-primary fs-5" />
                    <div>
                      <div className="small text-soft">Records Loaded</div>
                      <div className="fw-bold">{invoiceRows.length}</div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <BiRefresh className="text-success fs-5" />
                    <div>
                      <div className="small text-soft">Validated Revenue</div>
                      <div className="fw-bold">ZMW {totalRevenue.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Content>
    </>
  );
};

export default SmartInvoiceList;
